// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { isDefined } from '@shared/utils';
import { useCallback, useMemo } from 'react';
import { createDataProvider } from '@shared/ui-kit/providers/data-provider';
import { useSelector } from 'react-redux';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { HistValues, useStateValue } from '@shared/ui-kit';
import { useAccounts, useHistoricalBalances, usePeriodPicker } from '../hooks';
import {
  selectClient,
  selectClientInstitutionsByInstitutionId,
  selectClientInstitutionsIds,
  selectClientInstitutionByInstitutionId,
} from '@shared/data-access/slices/clients/clients-selectors';
import { selectUserId } from '@shared/data-access/slices/user';
import { isClientApp } from '@shared/environment';
import { useTagManagementData } from '@shared/tag-management';
import { useAccountsPositions } from '../hooks/use-accounts-positions';
import { useIlliquidAssets } from '../hooks/use-illiquid-assets';
import { filterIlliquidAssetsByTag } from '../utils/filter-illiquid-assets-by-tag';
import { useInstitutionSyncData } from './institution-sync-provider';
import {
  isManualCitecInstitution,
  isNuvaraInversisInstitution,
} from '@shared/data-access/utils';
import type { RefreshControlMode } from '../components/refresh-control';

export interface AggregationProviderProps {
  readonly institutionId: string;
  readonly clientId?: number;
}

export const useAggregationFactory = ({
  institutionId,
  clientId,
}: AggregationProviderProps) => {
  const institution = useSelector(state =>
    selectClientInstitutionByInstitutionId(state as any, institutionId),
  );

  const institutionIds = useSelector(selectClientInstitutionsIds);

  const {
    startSync,
    syncState: { isSyncing },
  } = useInstitutionSyncData();

  const { tags, tagsByObject } = useTagManagementData();

  const [selectedTag, setSelectedTag] = useStateValue<string | undefined>(
    `aggregator-selected-tag-${institutionId}`,
    () => undefined,
  );

  const [benchmark, setBenchmark] = useStateValue<{
    ticker: string;
    weight: number;
  }>('aggregator-benchmark-selector', { ticker: 'SPY', weight: 1 });

  const onChangeBenchmark = useCallback(
    (ticker: string) => {
      setBenchmark({
        ticker,
        weight: 1,
      });
    },
    [setBenchmark],
  );

  const periodPicker = usePeriodPicker(HistValues['3M']);

  const currency = useSelector(selectAccountCurrency);

  const institutions = useSelector((state: any) =>
    selectClientInstitutionsByInstitutionId(state, institutionId),
  );

  const { accountOptions, onChangeAccounts, selectedAccounts } = useAccounts(
    institutions,
    institutionId,
    clientId,
    selectedTag,
    tagsByObject,
  );

  const { accountsPositionsQuery, ...accountsPositions } = useAccountsPositions({
    clientId,
    ibans: selectedAccounts,
    institutionId,
    currency,
  });

  const activeClient = useSelector(selectClient);

  const userId = useSelector(selectUserId);

  const shouldLoadIlliquidAssets = useMemo(() => {
    if (isClientApp) return true;

    return (
      isDefined(activeClient) &&
      isDefined(userId) &&
      activeClient.citec_user_id === userId
    );
  }, [activeClient, userId]);

  const { illiquidAssets, illiquidAssetsQuery } = useIlliquidAssets({
    clientId,
    currency,
    skip: !shouldLoadIlliquidAssets,
  });

  const illiquidAssetsByTag = useMemo(
    () =>
      filterIlliquidAssetsByTag({
        assets: illiquidAssets,
        selectedTag,
        tagsByObject,
      }),
    [illiquidAssets, selectedTag, tagsByObject],
  );

  const illiquidAssetsStatus = useMemo(() => {
    const { isLoading, isError, isFetching } = illiquidAssetsQuery;

    return isLoading
      ? 'pending'
      : isError && !isFetching
      ? 'rejected'
      : isFetching
      ? 'fetching'
      : 'fulfilled';
  }, [illiquidAssetsQuery]);

  const hasHoldings = useMemo(() => {
    return accountsPositions.holdings.length > 0;
  }, [accountsPositions.holdings]);

  const {
    institutionsHistoricalBalances,
    historicalBalances,
    hasHistoricalBalances,
    accountsBalancesHistoricalQuery,
  } = useHistoricalBalances({
    clientId,
    end_date: periodPicker.end_date,
    start_date: periodPicker.start_date,
    ibans: selectedAccounts,
    institutionId,
    currency,
  });

  const historicalBalancesStatus = useMemo(() => {
    const { isLoading, isError, isFetching } = accountsBalancesHistoricalQuery;

    return isLoading
      ? 'pending'
      : isError && !isFetching
      ? 'rejected'
      : isFetching
      ? 'fetching'
      : 'fulfilled';
  }, [accountsBalancesHistoricalQuery]);

  const status = useMemo(() => {
    const isLoading = [
      accountsPositionsQuery.isLoading,
      accountsBalancesHistoricalQuery.isLoading,
    ].some(Boolean);

    const isError = [
      accountsPositionsQuery.isError,
      accountsBalancesHistoricalQuery.isError,
    ].some(Boolean);

    const isFetching = [
      accountsPositionsQuery.isFetching,
      accountsBalancesHistoricalQuery.isFetching,
    ].some(Boolean);

    return isLoading
      ? 'pending'
      : isError && !isFetching
      ? 'rejected'
      : isFetching
      ? 'fetching'
      : 'fulfilled';
  }, [
    accountsBalancesHistoricalQuery.isError,
    accountsBalancesHistoricalQuery.isFetching,
    accountsBalancesHistoricalQuery.isLoading,
    accountsPositionsQuery.isError,
    accountsPositionsQuery.isFetching,
    accountsPositionsQuery.isLoading,
  ]);

  const updatedDate = useMemo(() => {
    if (institutionId.toLocaleLowerCase() === 'all') {
      const mostRecentLastSync = institutions
        .filter(institution => !!institution.last_sync)
        .reduce((latest, institution) => {
          if (!latest || new Date(institution.last_sync!) > new Date(latest!)) {
            return institution.last_sync!;
          }

          return latest;
        }, null as string | null);

      return mostRecentLastSync;
    }

    return institutions.find(institution => institution.id === institutionId)
      ?.last_sync;
  }, [institutions, institutionId]);

  const shouldRefresh = useMemo(() => {
    return (
      (institution?.provider !== 'wealth_reader' &&
        institutionId.toLowerCase() !== 'all') ||
      institutionIds.length === 0 ||
      isManualCitecInstitution(institution) ||
      isNuvaraInversisInstitution(institution)
    );
  }, [institution, institutionId, institutionIds.length]);

  const refreshMode = useMemo<RefreshControlMode>(
    () => (shouldRefresh ? 'update' : 'sync'),
    [shouldRefresh],
  );

  const onRefresh = useCallback(() => {
    if (shouldRefresh) {
      accountsPositionsQuery.refetch();

      accountsBalancesHistoricalQuery.refetch();

      if (shouldLoadIlliquidAssets) {
        illiquidAssetsQuery.refetch();
      }

      return;
    }

    startSync(
      {
        client_id: clientId,
        connection_ids:
          institutionId.toLowerCase() === 'all' ? institutionIds : [institutionId],
      },
      institutionId,
      institution?.name ?? 'all',
    );
  }, [
    accountsBalancesHistoricalQuery,
    accountsPositionsQuery,
    illiquidAssetsQuery,
    shouldLoadIlliquidAssets,
    clientId,
    institutionId,
    institutionIds,
    institution?.name,
    shouldRefresh,
    startSync,
  ]);

  return {
    institutionId,
    onRefresh,
    benchmark,
    onChangeBenchmark,
    accountOptions,
    onChangeAccounts,
    selectedAccounts,
    tags,
    tagsByObject,
    selectedTag,
    setSelectedTag,
    status,
    institutionsHistoricalBalances,
    historicalBalances,
    hasHistoricalBalances,
    periodPicker,
    updatedDate,
    currency,
    clientId,
    accountsPositions,
    illiquidAssets,
    illiquidAssetsByTag,
    illiquidAssetsQuery,
    illiquidAssetsStatus,
    shouldLoadIlliquidAssets,
    hasHoldings,
    shouldRefresh,
    institutions,
    historicalBalancesStatus,
    isSyncing,
    refreshMode,
  } as const;
};

export const {
  useAggregationData,
  AggregationProvider,
  AggregationConsumer,
  AggregationDataContext,
  withAggregation,
} = createDataProvider({
  name: 'Aggregation',
  factory: useAggregationFactory,
});
